// GUIDED-FLOW: the ways into a flow besides its own page.
// - FlowBanner: the shell's slim line while the caller has open flows ("Complete your seat
//   setup, 3 of 5 chapters" · Open), dismissible for the session.
// - FlowCard: a `flow:<id>` card ref in chat (title, progress line, Open), so chat stays short.
// - SendSeatSetupDialog: a manager sends seat setup about a seat to a member, or invites someone
//   by email and sends it to the invitation (they fill their part once they accept).
// - OpenSeatSetup: the Seat setup modal's way into the seat's open run.

import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { ClipboardListIcon, LockIcon, XIcon } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { peopleQuery } from "../queries";
import { FormError } from "../structure/parts";
import { FlowModal } from "./flow-panel";
import {
  flowKeys,
  flowQuery,
  myOpenFlowsQuery,
  openRunOf,
  seatOpenFlowsQuery,
} from "./flow-queries";

const DISMISSED = "companyos.flow-banner.dismissed";

/** A two- or three-way choice as pressed buttons (each option may carry an (i)). */
export function ChoiceButtons<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: { value: T; label: string; info?: ReactNode }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset aria-label={label} className="m-0 flex flex-wrap items-center gap-2 border-0 p-0">
      {options.map((o) => (
        <span key={o.value} className="flex items-center gap-0.5" data-choice={o.value}>
          <Button
            type="button"
            size="sm"
            variant={value === o.value ? "secondary" : "outline"}
            aria-pressed={value === o.value}
            className={cn(value === o.value && "ring-1 ring-ring")}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
          {o.info ? <InfoTip label={`About ${o.label}`}>{o.info}</InfoTip> : null}
        </span>
      ))}
    </fieldset>
  );
}

function readDismissed(co: string): boolean {
  try {
    return sessionStorage.getItem(`${DISMISSED}.${co}`) === "1";
  } catch {
    return false;
  }
}

/** The shell's banner: the caller's open flows, one slim line. */
export function FlowBanner({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(myOpenFlowsQuery(api, company.id));
  const [dismissed, setDismissed] = useState(() => readDismissed(company.id));
  const [opened, setOpened] = useState<string | null>(null);
  const onFlowPage = useRouterState({
    select: (s) => s.location.pathname.includes("/flows/"),
  });
  const run = q.data?.data[0];
  return (
    <>
      {run && !dismissed && !onFlowPage ? (
        <section
          aria-label="Open flow"
          data-slot="flow-banner"
          className="flex items-center gap-2 border-b border-border bg-primary-soft px-4 py-1.5 text-sm"
        >
          <ClipboardListIcon aria-hidden="true" className="size-4 shrink-0 text-primary-text" />
          <span className="min-w-0 flex-1 truncate">{run.progress.line}</span>
          <Button size="xs" onClick={() => setOpened(run.id)}>
            Open
          </Button>
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label="Dismiss"
            onClick={() => {
              try {
                sessionStorage.setItem(`${DISMISSED}.${company.id}`, "1");
              } catch {
                // Session storage off: dismissed until reload.
              }
              setDismissed(true);
            }}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </section>
      ) : null}
      {opened ? (
        <FlowModal api={api} company={company} runId={opened} onClose={() => setOpened(null)} />
      ) : null}
    </>
  );
}

/** A `flow:<id>` card in a chat message. */
export function FlowCard({
  api,
  company,
  runId,
}: {
  api: Api;
  company: CompanyAccess;
  runId: string;
}) {
  const q = useQuery(flowQuery(api, company.id, runId));
  if (q.isPending)
    return (
      <span className="text-xs text-muted-foreground" role="status" aria-label="Loading flow">
        Loading…
      </span>
    );
  if (!q.data)
    return (
      <span
        data-slot="flow-card"
        data-state="hidden"
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
      >
        <LockIcon aria-hidden="true" className="size-3" />
        Restricted flow
      </span>
    );
  const run = q.data;
  return (
    <div
      data-slot="flow-card"
      className="flex max-w-md items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-sm"
    >
      <ClipboardListIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">
          {run.title} · {run.seat_title}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {run.status === "open"
            ? run.progress.line
            : run.status === "completed"
              ? "Submitted"
              : "Withdrawn"}
        </span>
      </span>
      {run.status === "completed" ? <Badge variant="success">Done</Badge> : null}
      <Link
        to="/c/$slug/flows/$id"
        params={{ slug: company.slug, id: run.id }}
        className={cn(buttonVariants({ size: "xs", variant: "outline" }), "shrink-0")}
      >
        Open
      </Link>
    </div>
  );
}

/** "Open seat setup" in the Seat setup modal, while the seat has an open run. */
export function OpenSeatSetup({
  api,
  company,
  seatId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
}) {
  const q = useQuery(seatOpenFlowsQuery(api, company.id, seatId));
  const [opened, setOpened] = useState<string | null>(null);
  const run = q.data?.data.find((r) => r.status === "open");
  if (!run) return null;
  return (
    <div className="flex items-center gap-2 text-sm" data-slot="open-seat-setup">
      <span className="min-w-0 flex-1 truncate text-muted-foreground">
        {run.assignee_name ? `${run.assignee_name} · ` : ""}
        {run.progress.chapters_done} of {run.progress.chapters_total} chapters
      </span>
      <Button variant="outline" size="xs" onClick={() => setOpened(run.id)}>
        Open seat setup
      </Button>
      {opened ? (
        <FlowModal api={api} company={company} runId={opened} onClose={() => setOpened(null)} />
      ) : null}
    </div>
  );
}

/** A manager sends seat setup about a seat: to a member, or to someone invited by email. */
export function SendSeatSetupDialog({
  api,
  company,
  seat,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: { id: string; title: string };
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [to, setTo] = useState<"member" | "invite">("member");
  const [member, setMember] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const people = useInfiniteQuery(peopleQuery(api, co));
  const members = (people.data?.pages ?? [])
    .flatMap((p) => p.data)
    .filter((p) => p.kind === "person" && p.status === "active");
  // One Idempotency-Key per attempt: a retry of the same send reuses it; changing who it goes
  // to starts a new attempt.
  const attempt = useRef<{ sig: string; key: string } | null>(null);
  const keyFor = (sig: string) => {
    if (attempt.current?.sig !== sig) attempt.current = { sig, key: newIdempotencyKey() };
    return attempt.current.key;
  };
  /** A seat setup already open on the seat (409): opened instead. */
  const [existing, setExisting] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => {
      const target =
        to === "member"
          ? { membership_id: member }
          : {
              // The invitation and the run are made in one write.
              invite: {
                email: email.trim(),
                display_name: name.trim() || email.trim(),
                roles: ["member" as const],
              },
            };
      return api.client.flows.send({
        params: { co },
        idempotencyKey: keyFor(JSON.stringify(target)),
        body: { flow_key: "seat_setup", seat_id: seat.id, ...target },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowKeys.all(co) });
      if (to === "invite") void qc.invalidateQueries({ queryKey: ["company", co, "invitations"] });
      toast.add({ title: "Seat setup sent", type: "success" });
      onClose();
    },
    onError: (e) => {
      const id = openRunOf(e);
      if (id) setExisting(id);
    },
  });
  const ok = to === "member" ? member !== "" : /^\S+@\S+\.\S+$/.test(email.trim());
  if (existing) return <FlowModal api={api} company={company} runId={existing} onClose={onClose} />;
  return (
    <QuickAddDialog
      title="Send seat setup"
      info="They fill their part (hours, tools, pay) in a guided flow; you fill the rest."
      description={seat.title}
      submitLabel="Send"
      canSubmit={ok}
      pending={send.isPending}
      onSubmit={() => send.mutate()}
      onClose={onClose}
      slot="send-seat-setup"
      error={<FormError what="Sending" error={send.error} />}
    >
      <ChoiceButtons
        label="Send to"
        value={to}
        options={[
          { value: "member", label: "Member" },
          { value: "invite", label: "Invite by email" },
        ]}
        onChange={setTo}
      />
      {to === "member" ? (
        <Field>
          <FieldLabel htmlFor="send-setup-member">Member</FieldLabel>
          <NativeSelect
            id="send-setup-member"
            value={member}
            onChange={(e) => setMember(e.target.value)}
          >
            <NativeSelectOption value="">Pick a member</NativeSelectOption>
            {members.map((p) => (
              <NativeSelectOption key={p.id} value={p.id}>
                {p.display_name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="send-setup-email">Email</FieldLabel>
            <Input
              id="send-setup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="send-setup-name">Name</FieldLabel>
            <Input id="send-setup-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
      )}
    </QuickAddDialog>
  );
}
